/**
 * Relatorios (BI) do OnPoint construidos no SeguraCore (P-BI-1 e P-BI-2).
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3,
 * docs/ADR/0043-relatorios-bi.md e o ADR das paginas Esteira, Endossos, Acumulo,
 * Consultas e Aprovacao (P-BI-2).
 */

import type { Money } from './comum';

import { api, requestBlob } from '../client';

export type ReportPageKey =
  | 'resumo'
  | 'cotacoes'
  | 'negociacao'
  | 'esteira'
  | 'apolices'
  | 'endossos'
  | 'tomadores'
  | 'carteira'
  | 'acumulo'
  | 'corretoras'
  | 'recorrencia'
  | 'consultas'
  | 'aprovacao';

export interface ReportPage {
  key: ReportPageKey;
  position: number;
  title: string;
  onpoint_title: string;
  package: 'P-BI-1' | 'P-BI-2';
  availability: 'disponivel' | 'pendente';
  pending_reason: string;
  reference_date: string;
  description: string;
  /** Relatorio numa ferramenta externa, so se o dono decidir incorporar (BI-386). */
  external_report_id?: string | null;
  /**
   * Pagina so da seguradora (Esteira, Acumulo e Aprovacao): com escopo de corretora, o
   * servidor responde 403, e a tela nao oferece a aba.
   */
  insurer_only?: boolean;
}

export type ReportMeasureUnit =
  'quantidade' | 'dinheiro' | 'pontos_base' | 'data' | 'dias' | 'texto' | 'metrica';

export interface ReportMeasure {
  key: string;
  position: number;
  version: number;
  label: string;
  onpoint_name: string;
  theme: string;
  unit: ReportMeasureUnit;
  status: 'implementada' | 'pendente';
  homologation: 'nao_homologada' | 'homologada';
  definition: string;
  pending_reason: string;
  rule_reference: string;
  source_view: string;
  package: string;
  matrix_ids: string;
}

export type ReportCellKind =
  | 'quantidade'
  | 'dinheiro'
  | 'pontos_base'
  | 'data'
  | 'data_hora'
  | 'dias'
  | 'texto'
  | 'documento'
  | 'booleano'
  /** Duracao em segundos (passagem pela esteira), escrita DD HH:MM:SS. */
  | 'duracao';

/**
 * Celula: o valor vem no campo do tipo. `pendente` e a medida sem definicao, que a
 * tela mostra como "pendente de definicao", e nunca como zero.
 */
export interface ReportCell {
  status: 'ok' | 'pendente' | 'vazio';
  kind: ReportCellKind;
  number?: number;
  money?: Money;
  text?: string;
  date?: string;
  datetime?: string;
  boolean?: boolean;
  masked?: boolean;
}

export interface ReportPathItem {
  key: string;
  label: string;
  masked: boolean;
}

export interface ReportVisualMeasure {
  key: string;
  label: string;
  kind: ReportCellKind;
  measure: string;
  pending: boolean;
  pending_reason: string;
  share_of: string;
}

export interface ReportVisualRow {
  level: number;
  path: ReportPathItem[];
  attributes: Record<string, ReportCell>;
  values: Record<string, ReportCell>;
}

export interface ReportVisual {
  key: string;
  kind: string;
  title: string;
  onpoint_title: string;
  note: string;
  pending: boolean;
  pending_reason: string;
  levels: { key: string; label: string }[];
  column: { key: string; label: string } | null;
  column_values: ReportPathItem[];
  attributes: { key: string; label: string; kind: ReportCellKind }[];
  measures: ReportVisualMeasure[];
  rows: ReportVisualRow[];
  truncated: boolean;
}

export interface ReportOption {
  key: string;
  label: string;
  pending?: boolean;
  /** O rotulo saiu mascarado por ser dado pessoal (o nome do responsavel). */
  masked?: boolean;
}

export interface ReportFilters {
  year: number[];
  month: number[];
  broker_id: string | null;
  policyholder_id: string | null;
  /** Identificador do grupo economico escolhido (nunca o CPF ou CNPJ); vazio sem filtro. */
  economic_group: string;
  status: string[];
  policy_status: string[];
  metric: string;
  // P-BI-2
  pipe?: string[];
  proposal_type?: string[];
  number?: string;
  owner_member_id?: string | null;
  endorsement_status?: string[];
  step?: number[];
  final_result?: string[];
  rule_status?: string[];
  rule?: string[];
}

/**
 * Segmentacao propria da pagina (Esteira, Endossos, Acumulo e Aprovacao): `lista`
 * liga e desliga varios valores, `unico` escolhe um, `texto` e digitado. A pendente
 * aparece com o motivo e nao filtra.
 */
export interface ReportSegmentation {
  key: string;
  param: string;
  title: string;
  kind: 'lista' | 'unico' | 'texto';
  options: ReportOption[];
  pending: boolean;
  pending_reason: string;
}

/** Detalhamento oferecido pela pagina; a Esteira, o Acumulo e a Aprovacao tem dois. */
export interface ReportDetailInfo {
  key: string;
  title: string;
  reference_date: string;
}

/** Botao de ajuda (Metodologia de apuracao de SLA, Manual de extracao). */
export interface ReportHelp {
  key: string;
  title: string;
  paragraphs: string[];
  pending_reason: string;
}

export interface ReportPageView {
  page: ReportPage;
  filters: ReportFilters;
  available_filters: string[];
  status_options: ReportOption[];
  policy_status_options: ReportOption[];
  metric_options: ReportOption[];
  generated_at: string;
  notices: string[];
  sections: { key: string; title: string; visuals: ReportVisual[] }[];
  measures: ReportMeasure[];
  // P-BI-2
  metric_title?: string;
  segmentations?: ReportSegmentation[];
  details?: ReportDetailInfo[];
  help?: ReportHelp[];
}

export interface ReportDetailColumn {
  key: string;
  label: string;
  kind: ReportCellKind;
  personal: boolean;
  pending: boolean;
  pending_reason: string;
}

export interface ReportDetails {
  page: ReportPage;
  /** O detalhamento entregue (a pagina pode ter mais de um). */
  detail?: string;
  title: string;
  reference_date: string;
  pending: boolean;
  pending_reason: string;
  columns: ReportDetailColumn[];
  items: Record<string, ReportCell>[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
  generated_at: string;
}

export interface ReportFilterOption {
  key: string;
  label: string;
  document: string;
  masked: boolean;
}

/** O filtro que a tela manda: listas viram o parametro repetido. */
export interface ReportQuery {
  year?: number[];
  month?: number[];
  broker_id?: string | undefined;
  policyholder_id?: string | undefined;
  /** Identificador do grupo economico: a `key` da pesquisa, e nao o documento. */
  economic_group?: string | undefined;
  status?: string[];
  policy_status?: string[];
  metric?: string | undefined;
  // P-BI-2
  pipe?: string[];
  proposal_type?: string[];
  number?: string | undefined;
  owner_member_id?: string | undefined;
  endorsement_status?: string[];
  step?: string[];
  final_result?: string[];
  rule_status?: string[];
  rule?: string | undefined;
  /** Qual detalhamento da pagina; sem ele, o padrao. */
  detail?: string | undefined;
  limit?: number;
  offset?: number;
  sort?: string | undefined;
}

/** Monta a query com o parametro repetido para as listas (year=2025&year=2026). */
export function reportQuery(filtro: ReportQuery): string {
  const busca = new URLSearchParams();
  for (const [chave, valor] of Object.entries(filtro)) {
    if (valor === undefined || valor === null || valor === '') continue;
    if (Array.isArray(valor)) {
      for (const item of valor) busca.append(chave, String(item));
      continue;
    }
    busca.set(chave, String(valor));
  }
  const texto = busca.toString();
  return texto ? `?${texto}` : '';
}

export const reports = {
  list: (signal?: AbortSignal) => api.get<ReportPage[]>('/api/v1/bi/reports', signal),
  measures: (signal?: AbortSignal) =>
    api.get<ReportMeasure[]>('/api/v1/bi/measures', signal),
  page: (pageKey: ReportPageKey, filtro: ReportQuery, signal?: AbortSignal) =>
    api.get<ReportPageView>(
      `/api/v1/bi/pages/${pageKey}${reportQuery(filtro)}`,
      signal,
    ),
  details: (pageKey: ReportPageKey, filtro: ReportQuery, signal?: AbortSignal) =>
    api.get<ReportDetails>(
      `/api/v1/bi/pages/${pageKey}/details${reportQuery(filtro)}`,
      signal,
    ),
  /** O arquivo leva o filtro sem paginacao: a exportacao e o detalhamento inteiro. */
  exportDetails: (pageKey: ReportPageKey, filtro: ReportQuery) =>
    requestBlob(`/api/v1/bi/pages/${pageKey}/details/export${reportQuery(filtro)}`),
  filterOptions: (
    kind: 'broker' | 'policyholder' | 'economic_group',
    q: string,
    signal?: AbortSignal,
  ) =>
    api.get<ReportFilterOption[]>(
      `/api/v1/bi/filter-options?kind=${kind}&q=${encodeURIComponent(q)}`,
      signal,
    ),
};
